import { MultiSelect } from '@shared/components/forms';
import {
  type StringOption,
  stringOptionsToValues,
  useStringOptions,
} from '@shared/helpers/string-options';
import { useCallback, useContext, useMemo } from 'react';
import { ProductTableContext } from '../../../contexts/product-table-context';
import type { Product } from '../../../data/product';
import { compare } from '../../../helpers/column-sort';
import type { Filters } from '../../../hooks/products';
import { withFilterField } from './filter-component';
import { FilterWrapper } from './filter-wrapper';

export const SizeFilter = withFilterField(
  'sizes',
  function SizeFilter({ products }: { products: Product[] }) {
    const { filters, setFilters } = useContext(ProductTableContext);
    const { sizes } = filters;

    const options: StringOption[] = useMemo(() => {
      // Create a map of size -> size_ounces for sorting
      const sizeToOunces = new Map<string, number>();
      for (const product of products) {
        if (product.size.trim() === '') {
          continue;
        }

        sizeToOunces.set(product.size.trim(), product.size_ounces);
      }

      // Convert to array and sort by size_ounces
      return [...sizeToOunces.entries()]
        .sort(([, ouncesA], [, ouncesB]) => compare(ouncesA, ouncesB))
        .map(([size]) => ({ value: size, label: size }));
    }, [products]);

    const value = useStringOptions(sizes);

    const onChange = useCallback(
      (sizeOptions: readonly StringOption[]) => {
        const sizes = stringOptionsToValues(sizeOptions);
        setFilters((prev: Filters) => ({ ...prev, sizes }));
      },
      [setFilters]
    );

    return (
      <FilterWrapper label="Size">
        <MultiSelect
          options={options}
          value={value}
          onChange={onChange}
          placeholder="Any"
          isMulti
        />
      </FilterWrapper>
    );
  }
);
